import type { TFunction } from 'i18next'
import type { Track } from 'src/types/domain/Track'

// Memoization cache for nameToKey results
const nameToKeyCache = new Map<string, string>()

/**
 * Convert track name to a valid translation key
 * Replaces spaces with underscores and converts to lowercase
 * Results are memoized for performance
 */
export const nameToKey = (name: string): string => {
  if (nameToKeyCache.has(name)) {
    return nameToKeyCache.get(name)!
  }

  const key = name
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')

  nameToKeyCache.set(name, key)
  return key
}

/**
 * Get translated game title using the track's name as translation key
 * Falls back to the original name if no translation exists
 */
export const getTranslatedGameTitle = (track: Track, t: TFunction): string => {
  const key = `title.${nameToKey(track.name)}`
  const translation = t(key, { ns: 'gameTracks' })

  if (translation !== key) {
    return translation
  }

  return track.name
}

/**
 * Get translated game description using the track's name as translation key
 * Falls back to the original description if no translation exists
 */
export const getTranslatedGameDescription = (track: Track, t: TFunction): string | null => {
  const key = `description.${nameToKey(track.name)}`
  const translation = t(key, { ns: 'gameTracks' })

  if (translation !== key) {
    return translation
  }

  return track.description
}

/**
 * Get translated marble color using the color name as translation key
 * Falls back to the original color name if no translation exists
 */
export const getTranslatedMarbleColor = (color: string, t: TFunction): string => {
  const key = nameToKey(color)
  const translation = t(key, { ns: 'marbleColors' })

  if (translation !== key) {
    return translation
  }

  return color
}
